/** Подсказка ⓘ к слову на экране: любое пояснение простыми словами, пришедшее готовым текстом. */
import { Info } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'

import './infoTip.css'

/**
 * Подсказка ⓘ к слову на экране — столбцу таблицы, полю, значению («Что значит “Статический”?»). Отличается от
 * <code>ActionInfo</code>: та описывает действие кнопки по записи реестра, а эта — любое пояснение простыми словами, которое
 * приходит готовым текстом. Нажал — открылось, Esc, щелчок мимо или повторное нажатие — закрылось; работает с клавиатуры и
 * диктором (`aria-expanded`, текст в `role="note"`), на телефоне мишень крупнее.
 */
export function InfoTip({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLSpanElement>(null)
  const noteId = useId()

  useEffect(() => {
    if (!open) return undefined
    const close = (event: Event) => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !(event.target instanceof Node && root.current?.contains(event.target) === true)) setOpen(false)
    }
    document.addEventListener('keydown', close)
    document.addEventListener('pointerdown', close)
    return () => {
      document.removeEventListener('keydown', close)
      document.removeEventListener('pointerdown', close)
    }
  }, [open])

  return (
    <span className="krp-info-tip" ref={root}>
      <button
        type="button"
        className="krp-info-tip__button"
        aria-label={`Что это: ${label}`}
        aria-expanded={open}
        aria-controls={open ? noteId : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <Info size={14} aria-hidden="true" />
      </button>
      {open && (
        <span id={noteId} role="note" className="krp-info-tip__note">
          <strong>{label}</strong>
          {children}
        </span>
      )}
    </span>
  )
}
